import moment from "moment";
import { CatatanPersalinan } from "@/types/partograf";

export type BayiSumber = "asesmen" | "catatan" | "gabungan";

export interface BayiInfo {
  key: string;
  judul: string;
  sumber: BayiSumber;
  nama: string;
  urutan: number;
  bayiLahir: string;
  prosesKelahiran: string;
  tanggalLahir: string;
  jamLahir: string;
  jenisKelamin: string;
  berat: string;
  panjang: string;
  apgar1: string;
  apgar5: string;
}

function formatTglLahir(jam: any): string {
  return jam ? moment(jam).format("DD-MM-YYYY") : "-";
}

function formatJamLahir(jam: any): string {
  return jam ? moment(jam).format("HH:mm") : "-";
}

function sexLabel(sex: any): string {
  if (sex === "L") return "Laki-laki";
  if (sex === "P") return "Perempuan";
  return "-";
}

function bayiLahirLabel(bayi: any): string {
  const status = bayi && bayi.status_bayi ? bayi.status_bayi : "-";
  let jenis = bayi && bayi.jenis_kelahiran ? bayi.jenis_kelahiran : "";
  if (jenis && bayi.urutan_kembar) jenis += " (ke-" + bayi.urutan_kembar + ")";
  return jenis ? status + " — " + jenis : status;
}

function isiAtau(val: any, fallback: string): string {
  if (val === null || val === undefined || String(val).trim() === "") return fallback;
  return String(val);
}

function kosong(val: string): boolean {
  return val === "-" || val === "";
}

function bblKosong(bbl: any): boolean {
  if (!bbl) return true;
  const kunci = ["bb_gram", "panjang_cm", "jenis_kelamin", "apgar_1menit", "apgar_5menit", "bayi_lahir_jam", "bayi_lahir_tipe", "kondisi_saat_lahir"];
  for (const k of kunci) {
    const v = bbl[k];
    if (v !== null && v !== undefined && String(v).trim() !== "") return false;
  }
  return true;
}

function tanggalCatatan(catatan: any, tanggalFallback?: string): string | null {
  const dari = catatan && catatan.info && catatan.info.tanggal ? catatan.info.tanggal : tanggalFallback;
  return dari ? moment(dari).format("YYYY-MM-DD") : null;
}

function jamLahirCatatan(bbl: any, catatan: any, tanggalFallback?: string): any {
  if (!bbl || !bbl.bayi_lahir_jam) return null;
  const tgl = tanggalCatatan(catatan, tanggalFallback);
  return tgl ? moment(tgl + " " + bbl.bayi_lahir_jam) : null;
}

function dariAsesmen(b: any, index: number): BayiInfo {
  return {
    key: b && b.id != null ? String(b.id) : "asesmen-" + index,
    judul: "Bayi",
    sumber: "asesmen",
    nama: b && b.pasien && b.pasien.nama ? b.pasien.nama : "",
    urutan: b && b.urutan_kembar ? Number(b.urutan_kembar) : index + 1,
    bayiLahir: bayiLahirLabel(b),
    prosesKelahiran: "-",
    tanggalLahir: formatTglLahir(b ? b.jam_lahir : null),
    jamLahir: formatJamLahir(b ? b.jam_lahir : null),
    jenisKelamin: sexLabel(b && b.pasien ? b.pasien.sex : null),
    berat: b && b.berat_lahir_gram ? b.berat_lahir_gram + " gram" : "-",
    panjang: b && b.panjang_badan_cm ? b.panjang_badan_cm + " cm" : "-",
    apgar1: "-",
    apgar5: "-"
  };
}

function dariCatatan(bbl: any, catatan: any, tanggalFallback?: string): BayiInfo {
  const jam = jamLahirCatatan(bbl, catatan, tanggalFallback);
  const tipe = bbl && bbl.bayi_lahir_tipe ? String(bbl.bayi_lahir_tipe) : "";
  const kondisi = bbl && bbl.kondisi_saat_lahir ? String(bbl.kondisi_saat_lahir) : "";

  return {
    key: "catatan",
    judul: "Bayi (dari Catatan Persalinan)",
    sumber: "catatan",
    nama: "",
    urutan: 1,
    bayiLahir: isiAtau(tipe || kondisi, "-"),
    prosesKelahiran: "-",
    tanggalLahir: jam ? jam.format("DD-MM-YYYY") : "-",
    jamLahir: bbl && bbl.bayi_lahir_jam ? String(bbl.bayi_lahir_jam) : "-",
    jenisKelamin: sexLabel(bbl ? bbl.jenis_kelamin : null),
    berat: bbl && bbl.bb_gram ? bbl.bb_gram + " gram" : "-",
    panjang: bbl && bbl.panjang_cm ? bbl.panjang_cm + " cm" : "-",
    apgar1: isiAtau(bbl ? bbl.apgar_1menit : null, "-"),
    apgar5: isiAtau(bbl ? bbl.apgar_5menit : null, "-")
  };
}

function lengkapiDariCatatan(row: BayiInfo, bbl: any, catatan: any, tanggalFallback?: string): BayiInfo {
  const isian = dariCatatan(bbl, catatan, tanggalFallback);
  const out = { ...row };
  let terisi = false;

  const medan: Array<keyof BayiInfo> = [
    "tanggalLahir",
    "jamLahir",
    "jenisKelamin",
    "berat",
    "panjang",
    "apgar1",
    "apgar5"
  ];

  for (const m of medan) {
    if (kosong(String(out[m])) && !kosong(String(isian[m]))) {
      (out as any)[m] = isian[m];
      terisi = true;
    }
  }

  if (kosong(out.bayiLahir) && !kosong(isian.bayiLahir)) {
    out.bayiLahir = isian.bayiLahir;
    terisi = true;
  }

  if (terisi) out.sumber = "gabungan";
  return out;
}

export function buildBayiInfo(
  bayiList: any[],
  catatan?: CatatanPersalinan | null,
  tanggalFallback?: string
): BayiInfo[] {
  const list = Array.isArray(bayiList) ? bayiList.filter(Boolean) : [];
  const bbl = catatan && (catatan as any).bayi_baru_lahir ? (catatan as any).bayi_baru_lahir : null;
  const adaCatatan = !bblKosong(bbl);

  if (!list.length) {
    return adaCatatan ? [dariCatatan(bbl, catatan, tanggalFallback)] : [];
  }

  const rows = list.map((b, i) => dariAsesmen(b, i));

  if (adaCatatan && rows.length === 1) {
    rows[0] = lengkapiDariCatatan(rows[0], bbl, catatan, tanggalFallback);
  }

  return rows;
}

export function catatanTidakDigabung(bayiList: any[], catatan?: CatatanPersalinan | null): boolean {
  const list = Array.isArray(bayiList) ? bayiList.filter(Boolean) : [];
  const bbl = catatan && (catatan as any).bayi_baru_lahir ? (catatan as any).bayi_baru_lahir : null;
  return list.length > 1 && !bblKosong(bbl);
}
